import React from 'react';
import { Divider, Typography, Grid, makeStyles } from '@material-ui/core';
import { images } from 'assets/images';
import RoomIcon from '@material-ui/icons/Room';
import CallIcon from '@material-ui/icons/Call';
import EmailIcon from '@material-ui/icons/Email';
import { COLOR, BASE_URL, COLOR_CONTRAST_RATIO } from 'config/constants';
import { getPhoneNumberWithFormatting, getColorContrastRatio } from 'utils';
import { useIntl } from 'react-intl';
import { PageLinks, SocialMediaIcons } from './index';

interface Props {
  style?: any;
  is_white_text?: boolean;
  portal_infos?: any;
  broker?: any;
  sections?: any;
  favorite_listings?: any;
}

function Footer(props: Props) {
  const intl = useIntl();
  const { style, is_white_text, portal_infos, broker, sections, favorite_listings } = props;

  const useStyles = makeStyles({
    row: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'space-between',
      '@media (max-width: 768px)': {
        flexDirection: 'column',
      },
    },
    marginRow: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 40,
      '@media (max-width: 820px)': {
        flexDirection: 'column',
        marginBottom: 10,
      },
    },
    footer: {
      padding: '70px 100px 50px 100px',
      '@media (max-width: 768px)': {
        padding: '50px 20px',
      },
    },
    image: {
      height: 30,
      '@media (max-width: 768px)': {
        height: 20,
        marginBottom: 5,
      },
    },
    broker_logo_image: {
      height: 40,
      '@media (max-width: 768px)': {
        height: 35,
      },
    },
    poweredBy: {
      marginLeft: 20,
      fontSize: 14,
      fontFamily: 'FordAntenna-Medium',
      '@media (max-width: 768px)': {
        fontSize: 13,
      },
    },
    text: {
      fontSize: 13,
      '@media (max-width: 768px)': {
        fontSize: 12,
      },
    },
    center: {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: 20,
      '@media (max-width: 768px)': {
        marginBottom: 10,
      },
    },
    infoText: {
      fontSize: 14,
      '@media (max-width: 768px)': {
        fontSize: 13,
      },
    },
    divider: {
      marginBottom: 30,
    },
    title: {
      textTransform: 'uppercase',
      marginBottom: 10,
      fontFamily: 'FordAntenna-Medium',
    },
    iconRow: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 5,
      flexDirection: 'row',
    },
    addressRow: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 5,
      flexDirection: 'row',
      justifyContent: 'center',
    },
    icon: {
      marginRight: 10,
    },
    socialMediaContent: {
      justifyContent: 'left',
      marginTop: 15,
    },
    gridItem: {
      '@media (max-width: 820px)': {
        marginBottom: 20,
      },
    },
  });

  const classes = useStyles({});

  const fmtAllRightReserved = intl.formatMessage({ id: 'portal.allRightReserved' });
  const fmtPoweredBy = intl.formatMessage({ id: 'portal.poweredBy' });
  const fmtContactMe = intl.formatMessage({ id: 'portal.contactMe' });
  const fmtQuickLinks = intl.formatMessage({ id: 'portal.quickLinks' });

  const text_color = is_white_text ? COLOR.WHITE : COLOR.BLACK;
  const footer_bg_color = portal_infos?.template_style?.header_bg_color || COLOR.ULTRA_DARK_BLUE;
  const brokerAdress = portal_infos?.address || broker?.centrisOfficeAddress;

  return (
    <div
      style={{
        background: footer_bg_color,
        color: text_color,
      }}
      className={classes.footer}
    >
      <Grid container className={classes.marginRow}>
        <Grid item md={4} sm={12} className={classes.gridItem}>
          <Typography className={classes.title}>{fmtContactMe}</Typography>
          {((portal_infos && portal_infos.phone_number) || broker.centrisPhoneNumber) && (
            <div className={classes.iconRow}>
              <CallIcon className={classes.icon} />
              <Typography className={classes.infoText}>
                {getPhoneNumberWithFormatting(
                  portal_infos.phone_number || broker.centrisPhoneNumber,
                )}
              </Typography>
            </div>
          )}
          {portal_infos && portal_infos.email && (
            <div className={classes.iconRow}>
              <EmailIcon className={classes.icon} />
              <Typography
                onClick={() => {
                  window.location.href = `mailto:${portal_infos.email}`;
                }}
                className={classes.infoText}
                style={{
                  cursor: 'pointer',
                  fontSize: 16,
                  fontWeight: 700,
                }}
              >
                {portal_infos.email}
              </Typography>
            </div>
          )}
          <SocialMediaIcons
            className={classes.socialMediaContent}
            portal_infos={portal_infos}
            broker={broker}
            show_white_icons={is_white_text}
          />
        </Grid>
        <Grid item md={4} sm={12} className={classes.gridItem}>
          {portal_infos.logo_image && (
            <div className={classes.center}>
              <img
                className={classes.broker_logo_image}
                src={portal_infos.logo_image || broker.centrisCompanyLogoUrl}
                alt='logo'
              />
            </div>
          )}
          {brokerAdress && (
            <div className={classes.addressRow}>
              <RoomIcon className={classes.icon} />
              <Typography className={classes.infoText}>{brokerAdress}</Typography>
            </div>
          )}
        </Grid>
        {broker.isPremium || broker.nonMemberPortalWebsite ? (
          <Grid item md={4} sm={12} className={classes.gridItem}>
            <Typography className={classes.title}>{fmtQuickLinks}</Typography>
            <PageLinks
              portal_infos={portal_infos}
              sections={sections}
              favorite_listings={favorite_listings}
              is_white_text={is_white_text}
              is_mobile={false}
              is_grid_view
              broker={broker}
            />
          </Grid>
        ) : null}
      </Grid>
      <Divider className={classes.divider} style={{ backgroundColor: text_color }} />
      <div className={classes.row}>
        <div
          className={classes.row}
          style={{ cursor: 'pointer' }}
          onClick={() => {
            window.open(BASE_URL, '_blank');
          }}
        >
          <img
            className={classes.image}
            src={is_white_text ? images.logoTitleWhite : images.logoTitle}
            alt='logo'
          />
          <Typography className={classes.poweredBy}>
            {fmtPoweredBy}
            <a
              href={BASE_URL}
              target='_blank'
              rel='noopener noreferrer'
              style={{
                marginLeft: 5,
                color: text_color,
                textDecoration: 'underline',
              }}
            >
              Hausvalet
            </a>
          </Typography>
        </div>
        <div>
          <Typography className={classes.text}>{fmtAllRightReserved}</Typography>
        </div>
      </div>
    </div>
  );
}

export default Footer;
